import { defineMessages, MessageDescriptor } from 'react-intl';
import { ReelFailReason } from '../../types';
import { aiBudgetMessages } from '../../aiBudgetMessages';

/** The copy of the wait screen and the failed screen (design `music24-ai-reels` frame b). */
export const reelProgressMessages = defineMessages({
  titleWorking: { id: 'app.sml.promote.reels.progress.title', defaultMessage: 'Making your reel' },
  titleFailed: { id: 'app.sml.promote.reels.progress.failed.title', defaultMessage: 'The reel did not finish' },
  titleReady: { id: 'app.sml.promote.reels.progress.ready.title', defaultMessage: 'Your reel is ready' },
  stageAnalysing: { id: 'app.sml.promote.reels.stage.analysing', defaultMessage: 'Analysing the song' },
  stagePlanning: { id: 'app.sml.promote.reels.stage.planning', defaultMessage: 'Planning the reel' },
  stageRendering: { id: 'app.sml.promote.reels.stage.rendering', defaultMessage: 'Rendering' },
  queuedLine: {
    id: 'app.sml.promote.reels.stage.queued.line',
    defaultMessage: 'Waiting to start. Your reel is next in line.',
  },
  analysingLine: {
    id: 'app.sml.promote.reels.stage.analysing.line',
    defaultMessage: 'We find the beats, the hook and the timing of each lyric line.',
  },
  analysingDone: {
    id: 'app.sml.promote.reels.stage.analysing.done',
    defaultMessage: 'We found the beats, the hook and the lyric timing.',
  },
  planningLine: {
    id: 'app.sml.promote.reels.stage.planning.line',
    defaultMessage: 'The AI picks the shots, writes the captions and the voiceover script.',
  },
  planningLineVoice: {
    id: 'app.sml.promote.reels.stage.planning.line.voice',
    defaultMessage: 'The AI picks the shots, writes the captions and the voiceover script for {voice}.',
  },
  planningLineNoVoice: {
    id: 'app.sml.promote.reels.stage.planning.line.noVoice',
    defaultMessage: 'The AI picks the shots and writes the captions.',
  },
  planningDone: {
    id: 'app.sml.promote.reels.stage.planning.done',
    defaultMessage: 'The AI wrote {count, plural, one {# caption} other {# captions}}.',
  },
  renderingLine: {
    id: 'app.sml.promote.reels.stage.rendering.line',
    defaultMessage: 'We put the reel together at 1080 × 1920.',
  },
  renderingNow: {
    id: 'app.sml.promote.reels.stage.rendering.now',
    defaultMessage: 'We put the reel together at 1080 × 1920. This is the longest step.',
  },
  stageDone: { id: 'app.sml.promote.reels.stage.done', defaultMessage: 'Done.' },
  stageStopped: { id: 'app.sml.promote.reels.stage.stopped', defaultMessage: 'Stopped.' },
  waitNote: {
    id: 'app.sml.promote.reels.progress.wait',
    defaultMessage:
      'A reel takes about 2 minutes. You can leave this page. The reel keeps going and shows under {release} when it is ready.',
  },
  thisRelease: { id: 'app.sml.promote.reels.progress.thisRelease', defaultMessage: 'the release' },
  failedPill: { id: 'app.sml.promote.reels.progress.failed.pill', defaultMessage: 'Failed' },
  refundLine: {
    id: 'app.sml.promote.reels.fail.refund',
    defaultMessage: 'You got the video credit back, so a new try costs nothing extra.',
  },
  noCredit: { id: 'app.sml.promote.reels.fail.noCredit', defaultMessage: 'No credit was used.' },
  tryAgain: { id: 'app.sml.promote.reels.fail.tryAgain', defaultMessage: 'Try again' },
  changeSong: { id: 'app.sml.promote.reels.fail.changeSong', defaultMessage: 'Change the song' },
  backToStudio: { id: 'app.sml.promote.reels.fail.back', defaultMessage: 'Back to the Studio' },
  changeSettings: { id: 'app.sml.promote.reels.fail.changeSettings', defaultMessage: 'Change the settings' },
});

/** The key of a fail sentence: a known `failReason`, or `unknown` for null. */
export type ReelFailKey = ReelFailReason | 'unknown';

/** One sentence per fail reason (design frame b4). */
export const reelFailMessages: Record<ReelFailKey, MessageDescriptor> = {
  ...defineMessages({
    SONG_TOO_SHORT: {
      id: 'app.sml.promote.reels.fail.songTooShort',
      defaultMessage: 'The song is too short for a reel. Use a song of 17 seconds or longer.',
    },
    UNUSABLE_AUDIO: {
      id: 'app.sml.promote.reels.fail.unusableAudio',
      defaultMessage: 'We could not read the audio in this file. Upload the song again as an mp3 or a wav.',
    },
    ANALYSIS_FAILED: {
      id: 'app.sml.promote.reels.fail.analysisFailed',
      defaultMessage: 'We could not analyse the song. Try again.',
    },
    DIRECTOR_FAILED: {
      id: 'app.sml.promote.reels.fail.directorFailedTheme',
      defaultMessage: 'The AI could not plan a reel for this song. Try again, or pick a different theme for the song.',
    },
    BUDGET_EXCEEDED: {
      id: 'app.sml.promote.reels.fail.budgetExceeded',
      defaultMessage: 'AI reels are paused for the rest of the month. Try again on the 1st.',
    },
    VOICEOVER_FAILED: {
      id: 'app.sml.promote.reels.fail.voiceoverFailed',
      defaultMessage:
        'We could not make the voiceover. Try again, pick a different voice, or make the reel with no voiceover.',
    },
    RENDER_FAILED: {
      id: 'app.sml.promote.reels.fail.renderFailed',
      defaultMessage: 'We could not put the reel together. Try again.',
    },
    RENDER_TIMEOUT: {
      id: 'app.sml.promote.reels.fail.renderTimeout',
      defaultMessage: 'The reel took too long to put together. Try again.',
    },
    STALLED: {
      id: 'app.sml.promote.reels.fail.stalled',
      defaultMessage: 'The reel stopped and did not finish. Try again.',
    },
    unknown: {
      id: 'app.sml.promote.reels.fail.unknown',
      defaultMessage: 'Something went wrong and the reel did not finish. Try again.',
    },
  }),
  AI_BUDGET_EXHAUSTED: aiBudgetMessages.failSentence,
};
